import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { projectsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Plus,
  Building2,
  Trash2,
  ArrowRight,
  Search,
  MapPin,
  Layers,
} from 'lucide-react';
import { toast } from 'sonner';

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    location: '',
    building_type: '',
    gross_floor_area: '',
    carbon_budget: '',
  });

  const buildingTypes = [
    'Residential',
    'Commercial Office',
    'Retail',
    'Industrial',
    'Healthcare',
    'Education',
    'Mixed Use',
    'Hospitality',
    'Cultural',
    'Infrastructure',
  ];

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await projectsAPI.getAll();
      setProjects(response.data);
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        ...formData,
        gross_floor_area: formData.gross_floor_area ? parseFloat(formData.gross_floor_area) : null,
        carbon_budget: formData.carbon_budget ? parseFloat(formData.carbon_budget) : null,
      };
      const response = await projectsAPI.create(payload);
      toast.success('Project created successfully');
      setIsDialogOpen(false);
      setFormData({
        name: '',
        description: '',
        location: '',
        building_type: '',
        gross_floor_area: '',
        carbon_budget: '',
      });
      navigate(`/projects/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (e, projectId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    
    try {
      await projectsAPI.delete(projectId);
      toast.success('Project deleted');
      fetchProjects();
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const filteredProjects = projects.filter((p) =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.building_type?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.location?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-8 h-8 border-4 border-[#0F2818] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="projects-page">
      {/* Header */}
      <div className="page-header -mx-8 -mt-8 mb-8 px-8 py-6 bg-white border-b border-slate-200">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Projects
            </h1>
            <p className="text-slate-600 mt-1">
              Manage your building projects and design options
            </p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#0F2818] hover:bg-[#06140C]" data-testid="create-project-button">
                <Plus className="w-4 h-4 mr-2" />
                New Project
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Create New Project</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Project Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Manchester Office Tower"
                    required
                    data-testid="project-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief project description"
                    data-testid="project-description-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="location">Location</Label>
                    <Input
                      id="location"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      placeholder="e.g., Manchester, UK"
                      data-testid="project-location-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="building_type">Building Type</Label>
                    <Select
                      value={formData.building_type}
                      onValueChange={(value) => setFormData({ ...formData, building_type: value })}
                    >
                      <SelectTrigger data-testid="project-type-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {buildingTypes.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="gross_floor_area">Gross Floor Area (m²)</Label>
                    <Input
                      id="gross_floor_area"
                      type="number"
                      value={formData.gross_floor_area}
                      onChange={(e) => setFormData({ ...formData, gross_floor_area: e.target.value })}
                      placeholder="e.g., 5000"
                      data-testid="project-area-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="carbon_budget">Carbon Budget (kgCO2e)</Label>
                    <Input
                      id="carbon_budget"
                      type="number"
                      value={formData.carbon_budget}
                      onChange={(e) => setFormData({ ...formData, carbon_budget: e.target.value })}
                      placeholder="e.g., 500000"
                      data-testid="project-budget-input"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-[#0F2818] hover:bg-[#06140C]"
                    disabled={creating}
                    data-testid="submit-project-button"
                  >
                    {creating ? 'Creating...' : 'Create Project'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-projects-input"
          />
        </div>
      </div>

      {/* Projects Grid */}
      {filteredProjects.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project) => (
            <Link to={`/projects/${project.id}`} key={project.id} data-testid={`project-card-${project.id}`}>
              <Card className="bento-card border-slate-200 h-full hover:border-slate-300 transition-colors cursor-pointer group">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-lg bg-[#E8EEEA] flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-[#0F2818]" />
                    </div>
                    <button
                      onClick={(e) => handleDelete(e, project.id)}
                      className="opacity-0 group-hover:opacity-100 p-2 text-slate-400 hover:text-red-500 transition-all"
                      data-testid={`delete-project-${project.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <h3 className="font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {project.name}
                  </h3>
                  
                  {project.description && (
                    <p className="text-sm text-slate-600 mb-4 line-clamp-2">
                      {project.description}
                    </p>
                  )}

                  <div className="space-y-2 mb-4">
                    {project.location && (
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <MapPin className="w-4 h-4" />
                        <span>{project.location}</span>
                      </div>
                    )}
                    {project.building_type && (
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Layers className="w-4 h-4" />
                        <span>{project.building_type}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                    <div>
                      <span className="text-xs text-slate-500 uppercase tracking-wider font-mono">
                        Design Options
                      </span>
                      <p className="text-lg font-bold text-slate-900">
                        {project.design_options?.length || 0}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 text-[#0F2818] opacity-0 group-hover:opacity-100 transition-opacity">
                      <span className="text-sm font-medium">View</span>
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Building2 className="w-16 h-16 text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {searchQuery ? 'No projects found' : 'No projects yet'}
            </h3>
            <p className="text-slate-600 mb-6">
              {searchQuery
                ? 'Try adjusting your search query'
                : 'Create your first project to start calculating embodied carbon'}
            </p>
            {!searchQuery && (
              <Button
                className="bg-[#0F2818] hover:bg-[#06140C]"
                onClick={() => setIsDialogOpen(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Project
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Projects;
